/**
 * CEO Command Center - Initiative Components
 * 
 * Components for displaying and managing initiatives:
 * - InitiativeCard: Compact card display
 * - InitiativeModal: Create/Edit modal
 * - InitiativeSidebarItem: Sidebar navigation item
 * 
 * @module components/features/initiatives
 * @version 10.1.0
 */

import React, { useState, useEffect } from 'react';
import { cn } from '../../../utils/format';
import { Button, Modal, Input, Select, Badge } from '../../ui';
import type {
  Initiative,
  InitiativeWithMetrics,
  InitiativeFormData,
  InitiativeStatus,
  InitiativePriority,
} from '../../../types/initiative';
import { INITIATIVE_ICONS, INITIATIVE_COLORS } from '../../../types/initiative';

// ============================================
// INITIATIVE CARD
// ============================================

interface InitiativeCardProps {
  initiative: InitiativeWithMetrics;
  isSelected?: boolean;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  compact?: boolean;
}

export const InitiativeCard: React.FC<InitiativeCardProps> = ({
  initiative,
  isSelected = false,
  onClick,
  onEdit,
  onDelete,
  compact = false,
}) => {
  const statusColors: Record<InitiativeStatus, string> = {
    Active: 'bg-green-100 text-green-700',
    Paused: 'bg-amber-100 text-amber-700',
    Completed: 'bg-blue-100 text-blue-700',
    Archived: 'bg-gray-100 text-gray-700',
  };
  
  if (compact) {
    return (
      <button
        onClick={onClick}
        className={cn(
          'w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left',
          isSelected
            ? 'bg-primary-500 text-white'
            : 'hover:bg-gray-100 text-gray-700'
        )}
      >
        <span className="text-lg">{initiative.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate text-sm">{initiative.name}</p>
          <p className={cn(
            'text-xs',
            isSelected ? 'text-primary-100' : 'text-gray-500'
          )}>
            {initiative.completedTasks}/{initiative.totalTasks} tasks
          </p>
        </div>
        {initiative.inProgressTasks > 0 && (
          <span className={cn(
            'w-2 h-2 rounded-full',
            isSelected ? 'bg-white' : 'bg-amber-500'
          )} />
        )}
      </button>
    );
  }
  
  return (
    <div
      className={cn(
        'p-4 rounded-xl border bg-white transition-all cursor-pointer',
        isSelected ? 'ring-2 ring-primary-500 border-primary-500' : 'hover:border-gray-300',
      )}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
            style={{ backgroundColor: initiative.color + '20' }}
          >
            {initiative.icon}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{initiative.name}</h3>
            {initiative.owner && (
              <p className="text-sm text-gray-500">{initiative.owner}</p>
            )}
          </div>
        </div>
        <Badge className={statusColors[initiative.status]}>
          {initiative.status}
        </Badge>
      </div>
      
      {/* Description */}
      {initiative.description && (
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
          {initiative.description}
        </p>
      )}
      
      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>Progress</span>
          <span>{initiative.progress}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${initiative.progress}%`,
              backgroundColor: initiative.color,
            }}
          />
        </div>
      </div>
      
      {/* Stats */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1 text-gray-600">
          <span>📋</span>
          <span>{initiative.totalTasks} tasks</span>
        </div>
        {initiative.inProgressTasks > 0 && (
          <div className="flex items-center gap-1 text-amber-600">
            <span>🟡</span>
            <span>{initiative.inProgressTasks} in progress</span>
          </div>
        )}
        {initiative.documentCount > 0 && (
          <div className="flex items-center gap-1 text-gray-600">
            <span>📄</span>
            <span>{initiative.documentCount} docs</span>
          </div>
        )}
      </div>
      
      {/* Actions */}
      {(onEdit || onDelete) && (
        <div className="flex gap-2 mt-3 pt-3 border-t">
          {onEdit && (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
            >
              ✏️ Edit
            </Button>
          )}
          {onDelete && !initiative.isDefault && (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="text-red-600 hover:bg-red-50"
            >
              🗑️ Delete
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// INITIATIVE MODAL
// ============================================

interface InitiativeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: InitiativeFormData) => void;
  initiative?: Initiative | null;
  title?: string;
}

export const InitiativeModal: React.FC<InitiativeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initiative,
  title,
}) => {
  const [formData, setFormData] = useState<InitiativeFormData>({
    name: '',
    description: '',
    owner: '',
    status: 'Active',
    priority: 'Medium',
    icon: '🚀',
    color: '#E85D4C',
    targetDate: null,
    tags: [],
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Reset form when modal opens/closes or initiative changes
  useEffect(() => {
    if (isOpen) {
      if (initiative) {
        setFormData({
          name: initiative.name,
          description: initiative.description || '',
          owner: initiative.owner || '',
          status: initiative.status,
          priority: initiative.priority,
          icon: initiative.icon,
          color: initiative.color,
          targetDate: initiative.targetDate || null,
          tags: initiative.tags || [],
        });
      } else {
        setFormData({
          name: '',
          description: '',
          owner: '',
          status: 'Active',
          priority: 'Medium',
          icon: '🚀',
          color: '#E85D4C',
          targetDate: null,
          tags: [],
        });
      }
      setErrors({});
    }
  }, [isOpen, initiative]);
  
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
      onClose();
    }
  };
  
  const updateField = <K extends keyof InitiativeFormData>(
    field: K,
    value: InitiativeFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title || (initiative ? 'Edit Initiative' : 'New Initiative')}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Icon and Color Row */}
        <div className="flex gap-4">
          {/* Icon Selector */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icon
            </label>
            <div className="flex flex-wrap gap-2 p-2 border rounded-lg max-h-24 overflow-y-auto">
              {INITIATIVE_ICONS.map(icon => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => updateField('icon', icon)}
                  className={cn(
                    'w-8 h-8 rounded flex items-center justify-center text-lg transition-all',
                    formData.icon === icon
                      ? 'bg-primary-100 ring-2 ring-primary-500'
                      : 'hover:bg-gray-100'
                  )}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
          
          {/* Color Selector */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Color
            </label>
            <div className="flex flex-wrap gap-2 p-2 border rounded-lg">
              {INITIATIVE_COLORS.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => updateField('color', color)}
                  className={cn(
                    'w-8 h-8 rounded-full transition-all',
                    formData.color === color ? 'ring-2 ring-offset-2 ring-gray-400' : ''
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* Name */}
        <Input
          label="Initiative Name"
          value={formData.name}
          onChange={e => updateField('name', e.target.value)}
          placeholder="e.g., AI Automation, Q1 Growth, Product Launch"
          error={errors.name}
          required
        />
        
        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={e => updateField('description', e.target.value)}
            placeholder="Brief description of this initiative..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>
        
        {/* Owner and Status Row */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Owner"
            value={formData.owner || ''}
            onChange={e => updateField('owner', e.target.value)}
            placeholder="Initiative lead"
          />
          
          <Select
            label="Status"
            value={formData.status}
            onChange={e => updateField('status', e.target.value as InitiativeStatus)}
            options={[
              { value: 'Active', label: 'Active' },
              { value: 'Paused', label: 'Paused' },
              { value: 'Completed', label: 'Completed' },
              { value: 'Archived', label: 'Archived' },
            ]}
          />
        </div>
        
        {/* Priority and Target Date Row */}
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Priority"
            value={formData.priority}
            onChange={e => updateField('priority', e.target.value as InitiativePriority)}
            options={[
              { value: 'Critical', label: 'Critical' },
              { value: 'High', label: 'High' },
              { value: 'Medium', label: 'Medium' },
              { value: 'Low', label: 'Low' },
            ]}
          />
          
          <Input
            label="Target Date"
            type="date"
            value={formData.targetDate || ''}
            onChange={e => updateField('targetDate', e.target.value || null)}
          />
        </div>
        
        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            {initiative ? 'Update' : 'Create'} Initiative
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ============================================
// INITIATIVE SIDEBAR SECTION
// ============================================

interface InitiativeSidebarProps {
  initiatives: InitiativeWithMetrics[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAdd: () => void;
  collapsed?: boolean;
}

export const InitiativeSidebar: React.FC<InitiativeSidebarProps> = ({
  initiatives,
  selectedId,
  onSelect,
  onAdd,
  collapsed = false,
}) => {
  const activeInitiatives = initiatives.filter(i => i.status === 'Active');
  
  if (collapsed) {
    return (
      <div className="py-2">
        {activeInitiatives.map(init => (
          <button
            key={init.id}
            onClick={() => onSelect(init.id)}
            className={cn(
              'w-full p-2 flex justify-center transition-colors',
              selectedId === init.id
                ? 'bg-primary-500 text-white'
                : 'text-gray-300 hover:bg-gray-800'
            )}
            title={init.name}
          >
            <span className="text-lg">{init.icon}</span>
          </button>
        ))}
        <button
          onClick={onAdd}
          className="w-full p-2 flex justify-center text-gray-500 hover:text-white hover:bg-gray-800"
          title="Add Initiative"
        >
          <span className="text-lg">➕</span>
        </button>
      </div>
    );
  }
  
  return (
    <div className="py-2">
      <p className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        Initiatives
      </p>
      
      {activeInitiatives.map(init => (
        <InitiativeCard
          key={init.id}
          initiative={init}
          isSelected={selectedId === init.id}
          onClick={() => onSelect(init.id)}
          compact
        />
      ))}
      
      <button
        onClick={onAdd}
        className="w-full flex items-center gap-3 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
      >
        <span className="text-lg">➕</span>
        <span className="text-sm">Add Initiative</span>
      </button>
    </div>
  );
};

// ============================================
// EXPORT
// ============================================

export default {
  InitiativeCard,
  InitiativeModal,
  InitiativeSidebar,
};
